import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Footer } from "risa-oasis-ui_v2";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { medicalPaSubmission } from "../../api/postCall/medicalPaSubmission";
import { submitFaxFormQuestionaire } from "../../api/postCall/submitFaxFormQuestionaire";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import { getMedicalPaOrderDetailsFor } from "../../redux/slice/medicalPa/singleMedicalPaOrder";
import { AppDispatch, RootState } from "../../redux/store/store";
import BackArrowBlue from "../../svg/back-arrow-blue";
import { flattenObjectToDotKeys } from "../../utils/createObjectFromDotNotation";
import { logError } from "../../utils/customLogger";
import { generateFullName } from "../../utils/generateOrderDataForTable";
import { capitalizeString } from "../../utils/stringModifications";
import FaxFormFields from "./components/faxFormFields/faxFormFields";
import { getGeneralFormConfig, getOncologyFormConfig } from "./formConfigs";
import { extractFirebaseValues } from "./utils/extractFirebaseValues";
import { transformFormDataToFirebase } from "./utils/transformFormDataToFirebase";

const FaxFormQuestionnaireLayout = () => {
  const { singleMedicalPaOrder, isLoading } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [enableSubmitButton, setEnableSubmitButton] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isFaxDataLoading, setIsFaxDataLoading] = useState<boolean>(false);
  const [faxQuestionnaireStatus, setFaxQuestionnaireStatus] = useState<
    string | null
  >(null);
  const { data: singleMedicalPaFormConfig } = useSelector(
    (state: RootState) => state.singleMedicalPaFormConfig,
  );

  const [selectedFormType, setSelectedFormType] = useState<
    "oncology" | "general" | null
  >(null);
  const [formData, setFormData] = useState<any>({});

  const { id } = useParams();

  const navigateBack = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    try {
      navigate(`/worklists/medical-pa-form/${id}`);
    } catch (error) {
      logError(error as Error);
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(getMedicalPaOrderDetailsFor(id));
    }
  }, [id, dispatch]);

  const getFormQuestions = async (orderId: string) => {
    try {
      setIsFaxDataLoading(true);
      console.log("Order ID from getFormQuestions:", orderId);
      const documentSnapshot = await FirestoreService.getDocument<any>(
        `/medical_pa_orders/${orderId}/questionnaire/fax_questionnaire`,
      );

      console.log(
        "Form Questions Data from fax getFormQuestions:",
        documentSnapshot,
      );
      if (documentSnapshot) {
        // Extract and set the status from the document
        setFaxQuestionnaireStatus(documentSnapshot.status || null);
      } else {
        setFaxQuestionnaireStatus(null);
      }
      return documentSnapshot;
    } catch (error) {
      console.log(error, "error not from slice");
      logError(error as Error, "Error fetching form questions");
      setFaxQuestionnaireStatus("error");
      return null;
    } finally {
      setIsFaxDataLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      getFormQuestions("4787a5e8-f456-4fa6-baa5-bf7404f0a796");
    }
  }, [id]);

  useEffect(() => {
    console.log("🚀 ~ singleMedicalPaFormConfig:", singleMedicalPaFormConfig);
    if (singleMedicalPaFormConfig?.id === "ph_medicare_nononcology_drug_form") {
      setSelectedFormType("general");
    } else if (
      singleMedicalPaFormConfig?.id === "ph_medicare_oncology_drug_form"
    ) {
      setSelectedFormType("oncology");
    }
  }, [singleMedicalPaFormConfig]);

  // Extract and populate form data from Firebase when order data AND selectedFormType are available
  useEffect(() => {
    console.log("🚀 ~ EXTRACTION useEffect TRIGGERED");
    console.log("🚀 ~ selectedFormType:", selectedFormType);
    console.log("🚀 ~ singleMedicalPaOrder exists:", !!singleMedicalPaOrder);
    console.log("🚀 ~ formData keys length:", Object.keys(formData).length);

    if (
      selectedFormType &&
      singleMedicalPaOrder &&
      Object.keys(formData).length === 0
    ) {
      console.log("🚀 ~ ALL CONDITIONS MET - Starting extraction");

      // Extract from both oncology and general configs to get all possible values
      const oncologyConfig = getOncologyFormConfig();
      const generalConfig = getGeneralFormConfig();

      const oncologyValues = extractFirebaseValues(
        singleMedicalPaOrder,
        oncologyConfig,
      );
      const generalValues = extractFirebaseValues(
        singleMedicalPaOrder,
        generalConfig,
      );

      // Merge values from both configs
      const extractedValues = { ...oncologyValues, ...generalValues };
      console.log("🚀 ~ Extracted oncology values:", oncologyValues);
      console.log("🚀 ~ Extracted general values:", generalValues);
      console.log("🚀 ~ Combined extracted values:", extractedValues);

      // Initialize with empty form structure and populate with extracted values
      const initialFormData = {
        // Initialize with empty values for all form fields
        nccnGuidelines: "",
        nccnGuidelinesName: "",
        nccnCategory: "",
        reviewedMBDL: "",
        medicalRecordsProvided: "",
        ecogScore: "",
        cancerStage: "",
        cancerStageT: "",
        cancerStageN: "",
        cancerStageM: "",
        therapySequence: "",
        biomarkerTesting: "",
        previousTreatments: "",
        requestedCondition: "",
        // Condition-specific fields
        bladderInvasive: "",
        bladderPlatinumEligible: "",
        bladderPdl1Positive: "",
        breastErPrPositive: "",
        breastHer2Status: "",
        breastEndocrineTherapy: "",
        cmlPhase: "",
        cmlRisk: "",
        cmlImatinibTried: "",
        colonUnresectable: "",
        colonKrasWildType: "",
        colonTumorsLeftSided: "",
        esophagealHistology: "",
        esophagealSurgicalCandidate: "",
        headNeckArea: "",
        mzlType: "",
        nsclcPdl1Positive: "",
        nsclcHistology: "",
        nsclcEgfrAlkMutation: "",
        nsclcNotTestedRationale: "",
        prostateCastrationStatus: "",
        prostateAdenocarcinoma: "",
        prostateMetastasesType: "",
        prostateProvengeLifeExpectancy: "",
        prostateProvengeAsymptomatic: "",
        rccCellType: "",
        sclcStage: "",
        otherCondition: "",
        // General form fields
        conditionRequested: "",
        isCancerTherapy: "",
        medicalReason: "",
        previousDrugs: "",
        additionalInformation: "",
        // Merge extracted values
        ...extractedValues,
      };

      console.log(
        "🚀 ~ Final isCancerTherapy value:",
        initialFormData.isCancerTherapy,
      );

      setFormData(initialFormData);
    } else {
      console.log("🚀 ~ ❌ CONDITIONS NOT MET - Extraction skipped");
      if (!selectedFormType) console.log("🚀 ~ Missing: selectedFormType");
      if (!singleMedicalPaOrder)
        console.log("🚀 ~ Missing: singleMedicalPaOrder");
      if (Object.keys(formData).length > 0)
        console.log(
          "🚀 ~ FormData not empty:",
          Object.keys(formData).length,
          "keys",
        );
    }
  }, [selectedFormType, singleMedicalPaOrder, formData]);

  const handleFormSubmit = async () => {
    if (!selectedFormType || !id) return;

    setIsSubmitting(true);

    try {
      // Get the form configuration
      const formConfig =
        selectedFormType === "oncology"
          ? getOncologyFormConfig()
          : getGeneralFormConfig();

      // Transform form data using firebaseKey mappings
      const firebaseFormData = transformFormDataToFirebase(
        formData,
        formConfig,
      );

      // Save form data to Firebase using core functionality
      const { FirestoreService } = await import(
        "../../api/firebase/firestoreService"
      );

      const flattenKeys = flattenObjectToDotKeys(firebaseFormData);

      // Update the medical pa order document with the form data
      await FirestoreService.updateDocument(
        "medical_pa_orders",
        id,
        flattenKeys,
      );
      console.log("🚀 ~ Firebase form data:", firebaseFormData);

      // First, fetch existing submission_required_details
      console.log("🚀 ~ Fetching existing submission_required_details...");
      const existingDocument = await FirestoreService.getDocument(
        `medical_pa_orders/${id}`,
      );
      const existingSubmissionDetails =
        (existingDocument as any)?.submission_required_details || {};

      console.log(
        "🚀 ~ Existing submission_required_details:",
        existingSubmissionDetails,
      );
      console.log("🚀 ~ New form data to merge:", firebaseFormData);

      // Extract and merge the submission_required_details
      const newSubmissionDetails =
        firebaseFormData.submission_required_details || {};
      const updatedSubmissionDetails = {
        ...existingSubmissionDetails,
        ...newSubmissionDetails,
      };

      console.log(
        "🚀 ~ New submission details from form:",
        newSubmissionDetails,
      );
      console.log(
        "🚀 ~ Final merged submission_required_details:",
        updatedSubmissionDetails,
      );

      // Update the document with the merged submission_required_details
      await FirestoreService.updateDocument("medical_pa_orders", id, {
        submission_required_details: updatedSubmissionDetails,
      });

      // Wait 2 seconds for Firebase data to persist
      console.log("🚀 ~ Waiting 2 seconds for Firebase data to persist...");
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Call medicalPaSubmission API with the formId
      const formId = singleMedicalPaFormConfig?.id;
      console.log("🚀 ~ Form ID:", formId);

      if (formId) {
        console.log(
          "🚀 ~ Calling medicalPaSubmission API with formId:",
          formId,
        );
        await medicalPaSubmission(id, formId);
        console.log("🚀 ~ medicalPaSubmission API call successful!");

        await submitFaxFormQuestionaire({
          form_id: formId,
          order_id: id,
        });

        // Wait 5 seconds for fax PDF generation
        console.log("🚀 ~ Waiting 5 seconds for fax PDF generation...");
        await new Promise((resolve) => setTimeout(resolve, 5000));

        // Fetch the fax PDF link from Firebase
        const { FirestoreService } = await import(
          "../../api/firebase/firestoreService"
        );

        console.log("🚀 ~ Fetching fax PDF link from Firebase...");
        const submissionInfoDoc = await FirestoreService.getDocument(
          `medical_pa_orders/${id}/submission_info/output`,
        );

        const faxPdfLink = (submissionInfoDoc as any)?.fax_pdf_link;
        console.log("🚀 ~ Fax PDF link:", faxPdfLink);

        // Navigate to FinalDocuments page with fax PDF link
        if (faxPdfLink) {
          navigate(`/worklists/final-document/${id}`, {
            state: { faxPdfLink },
          });
        } else {
          console.log("🚀 ~ No fax PDF link found, navigating without it");
          navigate(`/worklists/final-document/${id}`);
        }
      } else {
        console.log("🚀 ~ No formId found, skipping API call");
        // Still navigate if no formId (data was saved to Firebase)
        navigate(`/worklists/final-document/${id}`);
      }
    } catch (error) {
      logError(error as Error, "Error saving fax form data and submitting");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <LoaderMessage message="Loading order details..." />
      ) : (
        <div className="fax-form-questionnaire__container flex h-full flex-col bg-[#F7F9FA] px-[0.9375rem] py-[0.625rem]">
          <div className="fax-form-questionnaire__header mb-4">
            <div
              className="fax-form-questionnaire__header header-content flex items-center gap-1 text-h12 font-bold text-tertiaryBlue-4 hover:cursor-pointer"
              onClick={navigateBack}
            >
              <BackArrowBlue />
              Go Back
            </div>
          </div>

          <div className="fax-form-questionnaire__body flex flex-1 flex-col overflow-hidden rounded bg-white p-4 pb-0">
            <div className="fax-form-questionnaire__body--header mb-4">
              <div className="fax-form-questionnaire__body--header-details flex items-center gap-2">
                <div className="fax-form-questionnaire__body page-name text-large font-semiBold leading-6 text-black">
                  Fax Form Questionnaire
                  <span className="text-small font-semibold leading-6 text-primaryGray-10">
                    ( Priority Health Medicare Part B )
                  </span>
                </div>
                <div className="fax-form-questionnaire__body patient-name rounded-[1.875rem] bg-primaryGray-16 px-4 text-small font-semibold leading-6 text-primaryGray-1">
                  {capitalizeString(
                    generateFullName(
                      singleMedicalPaOrder?.demographics?.first_name ?? "",
                      "",
                      singleMedicalPaOrder?.demographics?.last_name ?? "",
                    ),
                  )}
                </div>
              </div>
            </div>

            <div className="fax-form-questionnaire__body content flex flex-1 overflow-hidden">
              {isFaxDataLoading ? (
                <div className="flex h-full w-full items-center justify-center">
                  <LoaderMessage message="Loading questionnaire..." />
                </div>
              ) : faxQuestionnaireStatus === "error" ? (
                <div className="flex h-full w-full flex-col items-center justify-center">
                  <div className="text-center">
                    <div className="mb-2 text-large font-semibold text-red-600">
                      Error Loading Questionnaire
                    </div>
                    <div className="mb-4 text-small text-gray-600">
                      There was an error loading the fax questionnaire. Please
                      try again.
                    </div>
                    <button
                      onClick={() => {
                        if (id) {
                          getFormQuestions(
                            "4787a5e8-f456-4fa6-baa5-bf7404f0a796",
                          );
                        }
                      }}
                      className="rounded bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              ) : faxQuestionnaireStatus === "in_progress" ? (
                <div className="flex h-full w-full items-center justify-center">
                  {/* <LoaderMessage message="Questionnaire is being processed..." /> */}
                  <div>In Progress</div>
                </div>
              ) : faxQuestionnaireStatus === "completed" ? (
                <FaxFormFields
                  formType={selectedFormType ?? "general"}
                  formData={formData}
                  onFormDataChange={setFormData}
                  onValidationChange={(isValid) =>
                    setEnableSubmitButton(isValid)
                  }
                  orderId={id ?? ""}
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <LoaderMessage message="Loading questionnaire..." />
                </div>
              )}
            </div>
          </div>

          {faxQuestionnaireStatus === "completed" && (
            <div className="fax-form-questionnaire__footer">
              <Footer
                cancelButtonText="Go Back"
                submitButtonText={
                  isSubmitting ? "Submitting..." : "Submit Form"
                }
                onCancelClick={() => navigateBack(null)}
                onSubmitClick={() => {
                  if (selectedFormType && !isSubmitting) {
                    handleFormSubmit();
                  }
                }}
                isDisabled={
                  !selectedFormType || !enableSubmitButton || isSubmitting
                }
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default FaxFormQuestionnaireLayout;
